import { useState } from 'react';
import { Copy, Loader2, Mail, ShieldOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { APP_CONFIG } from '@/config/app';
import { useTenant } from '@/hooks/useTenant';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import {
  portalAccessState,
  useCustomerPortalAccess,
  usePortalInvite,
  usePortalSetAccess,
} from '@/hooks/usePortalAccess';

interface CustomerPortalAccessCardProps {
  customer: { id: string; primary_email?: string | null };
}

/** Invite / resend / revoke the customer's self-service portal login. */
export function CustomerPortalAccessCard({ customer }: CustomerPortalAccessCardProps) {
  const { company } = useTenant();
  const { formatShortDate } = useCompanyLocale();
  const { data: link, isLoading } = useCustomerPortalAccess(customer.id);
  const invite = usePortalInvite();
  const setAccess = usePortalSetAccess();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const state = portalAccessState(link);
  const hasEmail = !!customer.primary_email?.trim();
  const portalUrl = company?.public_key ? `${APP_CONFIG.appUrl}/portal/${company.public_key}` : null;
  const busy = invite.isPending || setAccess.isPending;

  const copyLink = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      toast.success('Portal link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const badge = {
    not_invited: <Badge variant="secondary">Not invited</Badge>,
    invited: <Badge variant="info">Invited</Badge>,
    active: <Badge variant="success">Active</Badge>,
    revoked: <Badge variant="destructive-soft">Revoked</Badge>,
  }[state];

  const detail = (() => {
    if (isLoading) return 'Loading…';
    if (state === 'active' && link?.last_login_at) return `Last signed in ${formatShortDate(link.last_login_at)} · ${link.email}`;
    if (state === 'invited' && link) return `Link sent ${formatShortDate(link.last_link_sent_at ?? link.created_at)} · ${link.email}`;
    if (state === 'revoked' && link) return `Access revoked ${formatShortDate(link.revoked_at)} · ${link.email}`;
    return hasEmail
      ? 'The customer can sign in with their email to view quotes and invoices, accept a quote and pay online.'
      : 'Add an email address to the customer to invite them to the portal.';
  })();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">Customer portal</CardTitle>
          {badge}
        </div>
        <CardDescription>{detail}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {state === 'revoked' ? (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={busy || !link}
            onClick={() => link && setAccess.mutate({ linkId: link.id, customerId: customer.id, revoked: false })}
          >
            {setAccess.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Restore access
          </Button>
        ) : (
          <Button
            size="sm"
            className="gap-1.5"
            disabled={busy || !hasEmail}
            title={hasEmail ? undefined : 'The customer has no email address'}
            onClick={() => invite.mutate(customer.id)}
          >
            {invite.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {state === 'not_invited' ? 'Invite to portal' : 'Resend link'}
          </Button>
        )}
        {portalUrl && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => void copyLink()}>
            <Copy className="h-4 w-4" /> Copy portal link
          </Button>
        )}
        {(state === 'invited' || state === 'active') && link && (
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5 text-destructive hover:text-destructive"
            disabled={busy}
            onClick={() => setConfirmRevoke(true)}
          >
            <ShieldOff className="h-4 w-4" /> Revoke
          </Button>
        )}
      </CardContent>

      <AlertDialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke portal access?</AlertDialogTitle>
            <AlertDialogDescription>
              The customer will no longer be able to sign in to the portal. You can restore access later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => link && setAccess.mutate({ linkId: link.id, customerId: customer.id, revoked: true })}
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
